import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient, { fetchWithApiAuth } from "../lib/axios";
import { normalizeExplorerApiBaseUrl } from "../lib/apiBaseUrl";
import { getConfig } from "../lib/runtimeConfig";
import { getSearchErrorMessage } from "../lib/search/searchErrors";

/**
 * Parse Server-Sent Events from a stream. TCP chunks may split between lines,
 * so we must buffer until "\n\n" before interpreting an event (see search/parameters SSE).
 */
function parseNextSseEvent(buffer) {
  const sep = buffer.indexOf("\n\n");
  if (sep === -1) {
    return { rest: buffer, event: null };
  }
  const raw = buffer.slice(0, sep);
  const rest = buffer.slice(sep + 2);
  let eventType = "message";
  const dataLines = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) {
      continue;
    }
    if (line.startsWith("event:")) {
      eventType = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).replace(/^\s/, ""));
    }
  }
  const data = dataLines.join("\n");
  return { rest, event: { eventType, data } };
}

/**
 * The user-facing message for a failed request: always one of SEARCH_ERROR_MESSAGES, never server
 * text. The code (an APIError's string `detail`, or `detail.error` from /api/search/execute) only
 * picks which message.
 */
function messageFromAxiosError(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  const code = typeof detail === "string" ? detail : detail?.error;
  return getSearchErrorMessage({ status, code });
}

// Retry wrapper function with timeout
const executeWithRetry = async (
  functionParams,
  maxRetries = 2,
  timeout = 10000
) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), timeout);
      });

      const requestPromise = apiClient.post(
        "/api/search/execute",
        functionParams
      );

      const response = await Promise.race([requestPromise, timeoutPromise]);
      return response.data;
    } catch (error) {
      const isLastAttempt = attempt === maxRetries;
      const isTimeout = error.message === "Request timeout";

      if (isLastAttempt) {
        throw error;
      }

      if (isTimeout || error.code === "ECONNABORTED" || !error.response) {
        console.log(`Attempt ${attempt + 1} failed, retrying...`);
        await new Promise((resolve) =>
          setTimeout(resolve, Math.pow(2, attempt) * 1000)
        );
        continue;
      }

      throw error;
    }
  }
};

export const fetchResults = createAsyncThunk(
  "search/fetchResults",
  async (query, { dispatch, getState, rejectWithValue }) => {
    try {
      const known = getState().search.knownParse;
      if (known?.query === query) {
        try {
          dispatch(setParameters(known.parameters));
          dispatch(
            setParsingStatus({ stage: "cached", message: "Reusing the saved parse of this search..." })
          );
          return await executeWithRetry(known.parameters);
        } catch {
          // Stale (e.g. a metric renamed since it was saved): parse the text instead.
        }
      }

      const encodedQuery = encodeURIComponent(query);
      const apiBase = normalizeExplorerApiBaseUrl(getConfig("BASE_API_URL"));
      const searchUrl = apiBase
        ? `${apiBase}/api/search/parameters?query=${encodedQuery}`
        : `/api/search/parameters?query=${encodedQuery}`;
      const response = await fetchWithApiAuth(searchUrl, {
        maxRetries: 8,
        headers: {
          Accept: "text/event-stream",
          "Cache-Control": "no-cache",
        },
      });

      if (!response.ok) {
        let errorData = null;
        try {
          errorData = await response.json();
        } catch {
          // non-JSON body (e.g. a load balancer's HTML error page)
        }
        const { status } = response;
        // An APIError's `detail` is its code. Other errors can put free text there; that only
        // matters if it matches a code, and is never shown either way.
        const code = typeof errorData?.detail?.code === "string" 
          ? errorData.detail.code
          : typeof errorData?.detail === "string"
            ? errorData.detail
            : undefined;
        return rejectWithValue({ message: getSearchErrorMessage({ status, code }) });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const handleSseEvent = async (evt) => {
        const { eventType, data } = evt;
        if (eventType === "status" && data) {
          try {
            const payload = JSON.parse(data);
            dispatch(
              setParsingStatus({
                stage: payload.stage,
                message: payload.message,
              })
            );
          } catch {
            /* ignore malformed status */
          }
          return null;
        }
        if (eventType === "complete" && data) {
          let functionParams;
          try {
            functionParams = JSON.parse(data);
          } catch {
            return rejectWithValue({ message: "Invalid JSON in search complete event" });
          }
          dispatch(setParameters(functionParams));
          dispatch(setKnownParse({ query, parameters: functionParams }));
          try {
            return await executeWithRetry(functionParams);
          } catch (postError) {
            return rejectWithValue({
              message: messageFromAxiosError(postError),
            });
          }
        }
        if (eventType === "error" && data) {
          let payload = null;
          try {
            payload = JSON.parse(data);
          } catch {
            // non-JSON error event
          }
          const code = typeof payload?.error === "string" ? payload.error : "SEARCH_PARAMETERS_ERROR";
          return rejectWithValue({ message: getSearchErrorMessage({ code }) });
        }
        return null;
      };

      while (true) {
        const { done, value } = await reader.read();
        if (value) {
          buffer += decoder.decode(value, { stream: true });
        }
        if (done) {
          buffer += decoder.decode();
        }

        for (;;) {
          const { rest, event } = parseNextSseEvent(buffer);
          buffer = rest;
          if (!event) {
            break;
          }
          const outcome = await handleSseEvent(event);
          if (outcome !== null) {
            return outcome;
          }
        }

        if (done) {
          return rejectWithValue({
            message: "Search stream ended before results were ready",
          });
        }
      }
    } catch (error) {
      return rejectWithValue({ message: messageFromAxiosError(error) });
    }
  }
);

export const refetchAtLevel = createAsyncThunk(
  "search/refetchAtLevel",
  async (level, { getState, rejectWithValue }) => {
    const { parameters } = getState().search;
    try {
      return await executeWithRetry({
        metrics: parameters.metrics,
        filters: parameters.filters,
        places: parameters.places,
        level_override: level,
      });
    } catch (error) {
      return rejectWithValue({ message: messageFromAxiosError(error) });
    }
  }
);

const initialState = {
  // `null`, not a placeholder object -- every consumer already null-guards it, and a non-null
  // default would make SearchResults.js's tokenization effect fire before any real search ran,
  // or leave a previous search's stale parameters around for a query whose parse fails.
  // `fetchResults.pending` below also clears this at the START of a new search.
  parameters: null,
  results: [],
  // The query string that produced `results`. Returning from a district profile should show the
  // results again, not run the search a second time -- comparing this against the query in the URL
  // is how SearchResults tells "these are the results you already have" from "this is a new
  // search". Null whenever `results` is empty or came from something other than a query.
  resultsQuery: null,
  // The parse in hand for one query: set when a search is parsed or a saved search is picked, so
  // searching `query` again skips the LLM parse. SearchBar also uses it to keep the URL on `query`.
  knownParse: null,
  loading: false,
  error: null,
  query: "",
  // One-shot message from LandingPage's template cards to SearchBar's TokenizedSearchInput: the
  // raw `string | {pill}` segments to seed the bar with as real editable tokens (not just a
  // flattened string, which `query` above already covers for plainer writers like the Data
  // Catalog). The consumer clears this back to null right after reading it -- unlike `query`,
  // it's not meant to persist as ongoing state.
  pendingSegments: null,
  // The exact string|{pill} segments SearchBar's submit just recorded (see SearchBar.js's
  // handleSubmit) -- unlike `pendingSegments`, this isn't a one-shot message; it needs to survive
  // for the whole results-page visit so Save Search (SearchResults.js) can persist it. Cleared at
  // the start of a new search (fetchResults.pending, below) for the same reason `parameters` is:
  // a query whose parse never completes must not leave a previous, unrelated search's segments
  // around to be saved against it.
  lastSearchSegments: null,
  parsingStatus: {
    stage: null,
    message: null,
  },
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    setParameters: (state, action) => {
      state.parameters = action.payload;
    },
    resetParameters: (state) => {
      state.parameters = initialState.parameters;
    },
    clearError: (state) => {
      state.error = null;
    },
    setResults: (state, action) => {
      state.results = action.payload;
    },
    setQuery: (state, action) => {
      // Belongs to a different query now -- stale (e.g. Back/Forward). A caller that has real
      // segments for the new query dispatches setLastSearchSegments right after this, same tick.
      if (action.payload !== state.query) {
        state.lastSearchSegments = initialState.lastSearchSegments;
      }
      state.query = action.payload;
    },
    setKnownParse: (state, action) => {
      state.knownParse = action.payload;
    },
    setPendingSegments: (state, action) => {
      state.pendingSegments = action.payload;
    },
    setLastSearchSegments: (state, action) => {
      state.lastSearchSegments = action.payload;
    },
    setParsingStatus: (state, action) => {
      state.parsingStatus = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResults.pending, (state) => {
        state.loading = true;
        state.error = null;
        // Clears a previous query's stale parameters -- its parse may never complete.
        state.parameters = initialState.parameters;
        // NOT lastSearchSegments: this fires on every mount, including when handleSubmit just
        // paired it correctly with `query` moments ago. setQuery's own reducer handles its
        // staleness instead (same tick as the query change, no mount-timing race).
        state.parsingStatus = {
          stage: null,
          message: null,
        };
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.parsingStatus = {
          stage: "complete",
          message: null,
        };
        state.results = action.payload;
        // `meta` is absent when the action is built by hand rather than by the thunk.
        state.resultsQuery = action.meta?.arg ?? null;
      })
      .addCase(fetchResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || getSearchErrorMessage();
        state.parsingStatus = {
          stage: "error",
          message: action.payload?.message || getSearchErrorMessage(),
        };
        state.results = [];
        // A failed search must not leave a query marked as satisfied, or returning to the page
        // would show an empty table and never retry.
        state.resultsQuery = null;
      })
      .addCase(refetchAtLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refetchAtLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.results = action.payload;
      })
      .addCase(refetchAtLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || getSearchErrorMessage();
      });
  },
});

export const {
  setParameters,
  addParam,
  removeParam,
  resetParameters,
  clearError,
  setResults,
  setQuery,
  setKnownParse,
  setPendingSegments,
  setLastSearchSegments,
  setParsingStatus,
  setLoading,
} = searchSlice.actions;
export default searchSlice.reducer;
